import {
  Annotation,
  StatusCode,
  Status,
  Worker,
  User,
  Input,
  Data,
  Part,
  Frame,
  FrameInfo,
  Region,
  RegionInfo,
  Point,
  Concept,
  Struct,
  Image,
  Mask,
} from 'api/grpc/resources';
import { hasConcepts, hasRegions } from 'components/ModelOutputRenderer/components/utils';
import { isEmpty, isNil } from 'lodash';
import { updateConceptsFromAnnotationData } from 'utils/annotations/concept';
import { decomposeInternalAnnotationId, generateInternalAnnotationId, getConceptsFromAnnotationData } from 'utils/annotations/general';
import { convertJsAnnotationToGrpcAnnotation } from 'utils/grpc/jsToGrpc';
import { getIsAnnotationTrusted } from 'modules/Annotations/store/helpers';
import { makeUID } from 'utils/strings/strings';
import { MultiOutputResponse } from 'api/grpc/service';
import { FeatureFlagsType } from 'modules/FeatureFlagsLD/common';

// Import VideoMask type from annotations store
export type VideoMask = {
  frames: Record<string, string>;
  track: CF.API.Concept;
};

export type GetCreateAnnotationsRequestProps = {
  currentUserId: string;
  taskId?: string;
  statusCode?: StatusCode;
} & AnnotationsRequestProps;

export type AnnotationsRequestProps = {
  annotations: CF.API.Annotations.Annotation[];
  appId: string;
  userId: string;
  taskId?: string;
  inputId?: string;
};

export type AnnotationsResponseProps = {
  appId: string;
  userOrOrgId: string;
  annotations: Annotation[];
  action: 'overwrite' | 'merge' | 'remove';
  deleteIfEmptyData?: boolean | undefined;
};

export const getCreateAnnotationsRequest = ({
  annotations,
  appId,
  userId,
  currentUserId,
  taskId,
  statusCode = StatusCode.ANNOTATION_SUCCESS,
}: GetCreateAnnotationsRequestProps) => {
  const grpcAnnotations: Annotation[] = [];
  annotations.forEach((annotation) => {
    const worker = new Worker();
    const user = new User();
    const grpcAnnotation = convertJsAnnotationToGrpcAnnotation(annotation);
    grpcAnnotation.setId(annotation.id);
    grpcAnnotation.setInputId(annotation.input_id);

    // it was handling only user_id before, so we keep it like that
    if (currentUserId) {
      user.setId(currentUserId);
      worker.setUser(user);
      grpcAnnotation.setWorker(worker);
    }

    if (taskId) grpcAnnotation.setTaskId(taskId);
    grpcAnnotations.push(grpcAnnotation);
    const status = new Status();
    status.setCode(statusCode);
    grpcAnnotation.setStatus(status);
  });

  return {
    appId,
    userOrOrgId: userId,
    annotations: grpcAnnotations,
  };
};

export const getEditedAnnotationsReqest = ({ annotations, appId, userId }: AnnotationsRequestProps): AnnotationsResponseProps => {
  const grpcAnnotations: Annotation[] = [];

  annotations.forEach((annotation) => {
    const grpcAnnotation: Annotation = convertJsAnnotationToGrpcAnnotation(annotation);
    grpcAnnotation.setId(annotation.id);
    grpcAnnotation.setInputId(annotation.input_id);
    grpcAnnotations.push(grpcAnnotation);
  });

  return {
    appId,
    userOrOrgId: userId,
    annotations: grpcAnnotations,
    action: 'overwrite',
  };
};

export const getRemovedAnnotationsRequest = ({ annotations, appId, userId }: AnnotationsRequestProps): AnnotationsResponseProps => {
  const grpcAnnotations: Annotation[] = [];

  annotations.forEach((annotation) => {
    const grpcAnnotation: Annotation = convertJsAnnotationToGrpcAnnotation(annotation);
    grpcAnnotation.setId(annotation.id);
    grpcAnnotation.setInputId(annotation.input_id);
    grpcAnnotations.push(grpcAnnotation);
  });

  return {
    appId,
    userOrOrgId: userId,
    annotations: grpcAnnotations,
    action: 'remove',
    deleteIfEmptyData: true,
  };
};

export function transformSuggestionsToAnnotations({
  inputId,
  suggestions,
  taskId,
  model,
  workflow,
}: {
  inputId: string;
  suggestions: (CF.API.ModelOutputs.ModelOutput | CF.API.WorkflowOutputs.WorkflowOutput)[];
  taskId?: string;
  workflow?: CF.API.Workflows.Workflow;
  model?: CF.API.Models.Model;
}): CF.API.Annotations.Annotation[] {
  return suggestions
    .filter(({ data }) => !isNil(data) && !isEmpty(data))
    .map((suggestion) => {
      return {
        id: suggestion.id,
        input_id: inputId,
        created_at: suggestion.created_at,
        status: suggestion.status,
        isSuggestion: true,
        task_id: taskId,
        // user worker will always be empty as it is a suggestion coming from model or workfdlow
        worker:
          model || workflow
            ? {
                model,
                workflow,
              }
            : undefined,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        data: updateConceptsFromAnnotationData(suggestion.data!, (concepts) =>
          concepts.map((concept) => ({
            ...concept,
            name: concept.name.replaceAll(' ', '-'),
            app_id: 'main',
          })),
        ),
      };
    });
}

export function splitMultiRegionAnnotationsSuggestions({
  annotations,
  taskId,
}: {
  annotations: CF.API.Annotations.Annotation[];
  taskId?: string;
}): CF.API.Annotations.Annotation[] {
  return annotations.reduce<CF.API.Annotations.Annotation[]>((acc, annotation) => {
    const { data } = annotation;
    const baseData = {
      workerWorkflowId: annotation.worker?.workflow?.id ?? '',
      workerWorkflowVersionId: annotation.worker?.workflow?.version?.id ?? '',
      workerModelId: annotation.worker?.model?.id ?? '',
      workerModelVersionId: annotation.worker?.model?.model_version?.id ?? '',
      workerUserId: annotation.worker?.user?.id ?? '',
      taskId: taskId ?? '',
    };

    if (!hasRegions(data) || !data.regions) {
      acc.push(annotation);
      return acc;
    }

    if (data.regions.length <= 1) {
      acc.push(annotation);
      return acc;
    }

    const decomposed = data.regions.flatMap((region) => {
      const concepts = region.data.concepts ?? [];
      return concepts.map((concept) => {
        const myData = {
          ...baseData,
          id: annotation.id || '',
          regionId: region.id || '',
          conceptId: concept.id || '',
        };
        const internalId = generateInternalAnnotationId(myData);
        const modifiedAnnotation = {
          ...annotation,
          internalId,
          data: {
            ...annotation.data,
            regions: [
              {
                ...region,
                data: {
                  concepts: [concept],
                },
              },
            ],
          },
        };

        return modifiedAnnotation;
      });
    });

    acc.push(...decomposed);
    return acc;
  }, []);
}

export function filterOutAlreadyAcceptedSuggestions(
  suggestions: CF.API.Annotations.Annotation[],
  existingAnnotations: CF.API.Annotations.Annotation[],
): CF.API.Annotations.Annotation[] {
  return suggestions
    .map((suggestion) => {
      const { data } = suggestion;

      const uniqueRegions = hasRegions(data)
        ? (data.regions ?? []).filter((region) => {
            return !existingAnnotations.some((annotation) => {
              const { internalId: existingInternalId } = annotation;
              const isAnnotationTrusted = getIsAnnotationTrusted(annotation);
              // skip non-trusted annotations
              if (!isAnnotationTrusted || !existingInternalId) {
                return false;
              }
              const { regionId: existingRegionId } = decomposeInternalAnnotationId(existingInternalId);
              return region.id === existingRegionId;
            });
          })
        : [];

      const uniqueConcepts = hasConcepts(data)
        ? (data.concepts ?? []).filter((concept) => {
            return !existingAnnotations.some((existing) => {
              const existingConcept = getConceptsFromAnnotationData(existing.data)[0];
              return concept.id === existingConcept?.id;
            });
          })
        : [];

      if (uniqueRegions.length || uniqueConcepts.length) {
        return {
          ...suggestion,
          data: {
            ...data,
            ...(uniqueRegions.length ? { regions: uniqueRegions } : {}),
            ...(uniqueConcepts.length ? { concepts: uniqueConcepts } : {}),
          },
        };
      }

      return null;
    })
    .filter((a): a is CF.API.Annotations.Annotation => !!a);
}

export function filterSuggestionsByConceptNames(
  suggestions: CF.API.Annotations.Annotation[],
  allowedConceptNames: string[],
): CF.API.Annotations.Annotation[] {
  return suggestions.filter((suggestion) => {
    const suggestionData = suggestion.data;
    if (hasRegions(suggestionData) && suggestionData.regions) {
      suggestionData.regions = suggestionData.regions.filter((region) => {
        const regionConcepts = region.data.concepts;
        return regionConcepts?.some((regionConcept) => allowedConceptNames.some((filterConcept) => filterConcept === regionConcept.name));
      });
    }
    if (hasConcepts(suggestionData) && suggestionData.concepts) {
      suggestionData.concepts = suggestionData.concepts?.filter((concept) =>
        allowedConceptNames.some((filterConcept) => filterConcept === concept.name),
      );
    }
    return suggestionData;
  });
}

export function uint8ToBase64PNG(uint8Array: Uint8Array): Promise<string> {
  if (!uint8Array?.length) {
    return Promise.reject(new Error('Invalid Uint8Array input'));
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('loadend', () => {
      if (typeof reader.result === 'string') {
        return resolve(reader.result as string);
      } else {
        return reject(new Error('Incorrect format'));
      }
    });
    reader.addEventListener('error', () => reject(new Error('Failed to convert to Base64 PNG')));
    reader.readAsDataURL(new Blob([uint8Array as BlobPart], { type: 'image/png' }));
  });
}

function extractFramesMapFromAnnotations(annotations: CF.API.Annotations.Annotation[]): { [frameIndex: number]: CF.API.Annotations.Region[] } {
  const framesMap: { [frameIndex: number]: CF.API.Annotations.Region[] } = {};

  for (const annotation of annotations) {
    const baseData = annotation.data as CF.API.BaseFramesData;

    const trackId: string = baseData.metadata?.track_id;

    if (typeof baseData.metadata?.positive === 'boolean') {
      const value = Number(baseData.metadata.positive);
      const stringValue = value.toString();

      for (const frame of baseData.frames ?? []) {
        const frameIndex = frame.frame_info?.index;
        if (frameIndex !== undefined && frameIndex !== null) {
          const regions = (frame.data as CF.API.BaseRegionsData)?.regions ?? [];
          for (const region of regions) {
            const point: CF.API.Point | undefined = (region.region_info as CF.API.PointRegionInfo)?.point;
            if (point) {
              const videoRegion: CF.API.Annotations.Region = {
                id: makeUID(),
                region_info: { point: { row: point.row, col: point.col, z: 0 } },
                data: {
                  concepts: [{ id: stringValue, name: stringValue, value }],
                },
                track_id: trackId,
              };

              if (!framesMap[frameIndex]) framesMap[frameIndex] = [];
              framesMap[frameIndex].push(videoRegion);
            }
          }
        }
      }
    }
  }

  return framesMap;
}

const buildRegionFromVideoMaskData = (regionData: CF.API.Annotations.Region): Region => {
  const region: Region = new Region();

  const regionInfo: RegionInfo = new RegionInfo();
  const point: Point = new Point();
  const apiRegionInfo = regionData.region_info as CF.API.PointRegionInfo;
  point.setRow(apiRegionInfo.point.row);
  point.setCol(apiRegionInfo.point.col);
  regionInfo.setPoint(point);
  region.setRegionInfo(regionInfo);
  if (regionData.track_id !== undefined) region.setTrackId(regionData.track_id);

  const conceptData: Data = new Data();
  const newConcepts = (regionData.data?.concepts ?? []).reduce((acc: Concept[], c) => {
    const concept: Concept = new Concept();
    concept.setId(c.id);
    concept.setName(c.name);
    if (c.value !== undefined) concept.setValue(c.value);
    acc.push(concept);
    return acc;
  }, []);

  conceptData.setConceptsList(newConcepts);

  region.setData(conceptData);
  return region;
};

function buildFrameFromVideoMaskRegions(frameIndex: number, regions: CF.API.Annotations.Region[]): Frame {
  const frame: Frame = new Frame();
  const frameInfo: FrameInfo = new FrameInfo();
  frameInfo.setIndex(frameIndex);
  frame.setFrameInfo(frameInfo);

  const frameData: Data = new Data();
  frameData.setRegionsList(regions.map(buildRegionFromVideoMaskData));

  frame.setData(frameData);
  return frame;
}

function buildInputParts(currentInput: Input, framesList: Frame[]): [Part, Part] {
  const videoPart: Part = new Part();
  videoPart.setId('video');
  videoPart.setData(currentInput.getData());

  const framesPart: Part = new Part();
  framesPart.setId('frames');

  const framesData: Data = new Data();
  framesData.setFramesList(framesList);
  framesPart.setData(framesData);

  return [videoPart, framesPart];
}

export async function buildVideoMaskInput(
  annotations: CF.API.Annotations.Annotation[],
  currentInput: Input,
): Promise<{ input: Input; frameIndices: number[] }> {
  const framesMap = extractFramesMapFromAnnotations(annotations);

  const input: Input = new Input();
  input.setId(makeUID());

  const inputData: Data = new Data();
  inputData.setMetadata(Struct.fromJavaScript({ key: '_method_name', value: 'generate' }));

  const frameIndices: number[] = [];
  const framesList: Frame[] = Object.keys(framesMap).map((key: string) => {
    const frameIndex: number = Number.parseInt(key, 10);
    frameIndices.push(frameIndex);
    return buildFrameFromVideoMaskRegions(frameIndex, framesMap[frameIndex]);
  });

  const [videoPart, framesPart] = buildInputParts(currentInput, framesList);
  inputData.setPartsList([videoPart, framesPart]);
  input.setData(inputData);

  return { input, frameIndices };
}

// Create model request for video mask generation
export const createVideoMaskModelRequest = async (
  filteredAnnotations: CF.API.Annotations.Annotation[],
  currentInput: Input,
  videoMaskGenerationConfig: FeatureFlagsType['videoMaskGenerationConfig'],
) => {
  const { input } = await buildVideoMaskInput(filteredAnnotations, currentInput);

  if (videoMaskGenerationConfig.APP_ID === '' || videoMaskGenerationConfig.MODEL_ID === '' || videoMaskGenerationConfig.MODEL_VERSION_ID === '') {
    throw new Error('Video mask generation configuration is incomplete. Please check the request config.');
  }
  return {
    inputs: [input],
    userOrOrgId: videoMaskGenerationConfig.USER_OR_ORG_ID,
    appId: videoMaskGenerationConfig.APP_ID,
    modelId: videoMaskGenerationConfig.MODEL_ID,
    modelVersionId: videoMaskGenerationConfig.MODEL_VERSION_ID,
  };
};

export const groupFramedPointAnnotationsByConcept = (
  annotaions: CF.API.Annotations.Annotation[],
): Record<string, { concept: CF.API.Concept; annotations: CF.API.Annotations.Annotation[] }> => {
  const conceptGroups: Record<string, { concept: CF.API.Concept; annotations: CF.API.Annotations.Annotation[] }> = {};

  annotaions.forEach((annotation) => {
    const frames = (annotation.data as CF.API.BaseFramesData).frames;
    if (!frames?.length) return;

    frames.forEach((frame) => {
      const regions = (frame.data as CF.API.BaseRegionsData)?.regions;
      if (!regions?.length) return;

      regions.forEach((region) => {
        const hasPoint = 'point' in region.region_info;
        if (!hasPoint) return;

        const concepts = region.data?.concepts;
        if (!concepts?.length) return;

        concepts.forEach((concept) => {
          // TODO: check, strange that concept.id can be missing here, why?
          const conceptId = concept.id;
          if (!conceptId) return;

          if (!conceptGroups[conceptId]) {
            conceptGroups[conceptId] = { concept, annotations: [] };
          }
          conceptGroups[conceptId].annotations.push(annotation);
        });
      });
    });
  });

  return conceptGroups;
};

export const handleVideoMaskResponse = async (response: MultiOutputResponse, frameMasksMap: Record<string, string>): Promise<void> => {
  const outputsList = response.getOutputsList();
  if (outputsList.length === 0) return;

  const firstOutput = outputsList[0];
  const framesList = firstOutput.getData()?.getPartsList()[0]?.getData()?.getFramesList();
  if (!framesList || framesList.length === 0) return;

  const firstFrame = framesList[0];
  const frameIndex = firstFrame.getFrameInfo()?.getIndex();
  if (frameIndex === undefined) return;

  const maskBase64 = firstFrame.getData()?.getRegionsList()[0]?.getRegionInfo()?.getMask()?.getImage()?.getBase64();
  if (maskBase64 === undefined) {
    console.error('Mask base64 is undefined, Uint8Array is expected');
    return;
  }
  if (typeof maskBase64 === 'string') {
    // BE sends us Unit8Array base64 data, while FE constructs string base64 data to send a request
    console.error('String has been passed to mask response handler. Check if you are using it with RESPONSE.');
    return;
  }

  try {
    const pngResponse = await uint8ToBase64PNG(maskBase64);
    frameMasksMap[String(frameIndex)] = pngResponse;
  } catch (error) {
    console.error('Error converting mask base64 to png:', error);
  }
};

// Create annotation from video mask frame
export const createAnnotationFromVideoMaskFrame = (
  frameIndex: string,
  base64FrameData: string,
  trackConcept: CF.API.Concept,
  inputId: string,
  userId: string,
): Annotation => {
  const image = new Image().setBase64(base64FrameData.replace(/^data:image\/\w+;base64,/, ''));
  const mask = new Mask().setImage(image);
  const regionInfo = new RegionInfo().setMask(mask);
  const concept = new Concept().setId(trackConcept.id).setName(trackConcept.name);
  const regionData = new Data().setConceptsList([concept]);
  const region = new Region().setRegionInfo(regionInfo).setData(regionData);
  const frame = new Frame().setFrameInfo(new FrameInfo().setIndex(Number.parseInt(frameIndex, 10))).setData(new Data().setRegionsList([region]));
  const annotation = new Annotation()
    .setId(makeUID(16))
    .setInputId(inputId)
    .setUserId(userId)
    .setData(new Data().setFramesList([frame]));

  return annotation;
};

export const convertVideoMasksToAnnotations = (videoMasks: VideoMask[], inputId: string, userId: string): Annotation[] => {
  const allAnnotations: Annotation[] = [];
  console.log({ videoMasks });
  videoMasks.forEach((mask) => {
    if (mask.frames) {
      Object.entries(mask.frames as Record<string, string>).forEach(([frameIndex, frameData]) => {
        const annotation = createAnnotationFromVideoMaskFrame(frameIndex, frameData, mask.track, inputId, userId);
        allAnnotations.push(annotation);
      });
    }
  });

  return allAnnotations;
};

export const splitAnnotationsIntoChunks = (annotations: Annotation[], chunkSize: number): Annotation[][] => {
  const chunks: Annotation[][] = [];

  for (let i = 0; i < annotations.length; i += chunkSize) {
    chunks.push(annotations.slice(i, i + chunkSize));
  }

  return chunks;
};
